import { useState, useEffect, useId } from "react";
import { ChevronLeft, Search, Store, X, Zap, MoreVertical, Upload, HelpCircle, Navigation as NavigationIcon, ShoppingCart, Home, Download } from "lucide-react";
import { Link } from "react-router-dom";
import SearchBar from "./SearchBar";
import FilterButtons from "./FilterButtons";
import CategoryFilters from "./CategoryFilters";
import { Button } from "@/components/ui/button";
import { updateRapidInsertFlag } from "@/services/shopping/offline-lists.service";
import { useShops } from "@/hooks/useShops";
import usePremiumFeatures from "@/hooks/usePremiumFeatures";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { getCategoryOrderingForShop } from "@/services/shops.service";
import { useQuery } from "@tanstack/react-query";
import { updateShoppingCategoriesFromShopOrdering } from "@/services/categories.service";
import logger from "@/utils/logger";
import { useThemeConfig, DEFAULT_THEME } from "@/components/ui/theme-config-provider";
import { Badge } from "@/components/ui/badge";
import { useTutorialManager } from "@/hooks/useTutorialManager";
import { useLocation } from "react-router-dom";
import { shoppingHeaderTutorial } from "@/components/tutorial/ShoppingHeaderTutorial";
import TutorialSpotlight from "@/components/tutorial/TutorialSpotlight";
import { networkService } from '@/services/network.service';
import OfflineLogger from '@/utils/offlineLogger';
import { offlineDb } from '@/db/offlineDb';
import VisualDebugger from '@/components/debug/VisualDebugger';
import useDebugPreferences from "@/hooks/useDebugPreferences";
import ImportItemsDialog from "@/components/shopping/ImportItemsDialog";
import ImportListDialog from "./ImportListDialog";

interface ShoppingHeaderProps {
  listId: string;
  rapidInsert?: boolean;
  onRapidInsertChange?: (newValue: boolean) => void;
  listName: string;
  searchOpen: boolean;
  searchTerm: string;
  filter: string;
  selectedCategories: string[];
  setSearchOpen: (open: boolean) => void;
  setSearchTerm: (term: string) => void;
  setFilter: (filter: string) => void;
  setSelectedCategories: (categories: string[]) => void;
  isPrimary: boolean;
  hasMultipleLists: boolean;
  shopId?: string;
  onShopSelect: (shopId: string | null) => void;
  onTutorialStepChange?: (target: string | null) => void;
  mode: 'spesa' | 'preparazione';
  onModeChange: (mode: 'spesa' | 'preparazione') => void;
  isImportDialogOpen: boolean;
  onImportDialogOpenChange: (open: boolean) => void;
  onExportList: () => void | Promise<void>;
  canExport: boolean;
}

const ShoppingHeader = ({
  listName,
  searchOpen,
  searchTerm,
  filter,
  selectedCategories,
  setSearchOpen,
  setSearchTerm,
  setFilter,
  setSelectedCategories,
  isPrimary,
  hasMultipleLists,
  listId,
  onRapidInsertChange,
  shopId,
  onShopSelect,
  onTutorialStepChange,
  mode,
  onModeChange,
  isImportDialogOpen,
  onImportDialogOpenChange,
  onExportList,
  canExport,
}: ShoppingHeaderProps) => {
  const [rapidInsert, setRapidInsert] = useState(false);
  const { theme } = useThemeConfig();
  const { tryUseFeature } = usePremiumFeatures();
  const [isOnline, setIsOnline] = useState(networkService.isOnline());
  const spesaDescId = useId();
  const preparazioneDescId = useId();
  const { visualDebuggerEnabled, queryOverrideActive } = useDebugPreferences();
  const showVisualDebugger = visualDebuggerEnabled || queryOverrideActive;
  
  const { shops } = useShops();

  const location = useLocation();
  const path = location.pathname; // es: "/recurring-products"
  const pageId = `tutorial${path.replace(/\//g, '-')}`; // es: "tutorial--recurring-products"
  const { isOpen, openTutorial, closeTutorial, isAnyTutorialOpen, closeAnyTutorial } = useTutorialManager({
    pageId,
    showOnFirstVisit: true
  });

  const handleOpenTutorial = () => {
    if (isAnyTutorialOpen) {
      closeAnyTutorial();
    }
    openTutorial();
  };

  // Listen for network status changes
  useEffect(() => {
    const unsubscribe = networkService.addListener((status) => {
      setIsOnline(status.connected);
    });
    
    return () => {
      unsubscribe();
    };
  }, []);

  // Load category ordering for selected shop
  const { data: shopCategoryOrdering } = useQuery({
    queryKey: ["category-ordering", shopId],
    queryFn: () => shopId ? getCategoryOrderingForShop(shopId) : null,
    enabled: !!shopId && isOnline, // Only run if online
  });

  const handleToggleSearch = () => {
    setSearchOpen(!searchOpen);
  };

  const toggleRapidInsert = async () => {
    try {
      const newValue = !rapidInsert;
      setRapidInsert(newValue); // Optimistically update UI
      
      const success = await updateRapidInsertFlag(listId, newValue);
      
      if (success) {
        //OfflineLogger.info('ShoppingHeader', `Rapid insert updated to ${newValue}`);
        onRapidInsertChange?.(newValue);
      } else {
        // Revert UI if failed
        setRapidInsert(rapidInsert);
        OfflineLogger.error('ShoppingHeader', 'Failed to update rapid insert');
      }
    } catch (error) {
      // Revert UI if error
      setRapidInsert(rapidInsert);
      OfflineLogger.error('ShoppingHeader', `Error toggling rapid insert: ${error}`);
    }
  };
  
  useEffect(() => {
    // Try to get rapid_insert status from local database
    const fetchList = async () => {
      try {
        // Load from offline DB first (guaranteed to work online or offline)
        const offlineList = await offlineDb.shoppingLists.get(listId);
        
        if (offlineList) {
          setRapidInsert(offlineList.rapid_insert || false);
         // OfflineLogger.info('ShoppingHeader', `Loaded rapid insert status from offline DB: ${offlineList.rapid_insert}`);
        } else {
          //OfflineLogger.info('ShoppingHeader', 'List not found in offline DB');
        }
      } catch (error) {
        OfflineLogger.error('ShoppingHeader', `Error fetching list data: ${error}`);
      }
    };

    fetchList();
  }, [listId]);
  
  const selectedShop = (shops && shopId)
  ? shops.find(shop => shop.id === shopId)
  : null;

  const { associateListWithShop } = useShops();
  //logger.log("Selected shop:", shops, shopId, selectedShop);
  return (
    <>
    <header
      className="fixed top-0 left-0 right-0 z-50 shadow-md transition-all duration-200 ease-in-out hover:bg-[var(--btn-bg-hover)]"
      style={{
        background: theme.headerGradient ?? theme.primaryColor ?? theme.backgroundColor,
        color: theme.textPrimaryColor,
        transition: `all ${theme.transitionSpeed || '0.2s'} ease`,
        boxShadow: theme.boxShadow,
        '--btn-bg-hover': theme.primaryColor,
      } as React.CSSProperties}
    >
      <div
        className="container flex items-end justify-between max-w-md px-4"
        style={{
          // Extra space avoids the status bar or notch covering the header
          paddingTop: 'calc(var(--safe-area-top) + 30px)',
          paddingBottom: '0.75rem',
        }}
      >
        <div className="flex items-end gap-1">
        <Link to="/lists" className="p-1 -ml-1">
          <ChevronLeft className="pb-1 h-5 w-5" />
        </Link>
        <div className="flex flex-col justify-end truncate">
          <h1 className="text-lg font-semibold truncate">{listName}</h1>
          <div className="flex gap-2 mt-1">
            <Button
              onClick={() => onModeChange('spesa')}
              className={`tutorial-target-shoppingheader-mode h-auto px-2 py-1 text-xs flex items-center gap-1 ${mode === 'spesa' ? '' : 'opacity-50'}`}
              aria-pressed={mode === 'spesa'}
              aria-describedby={spesaDescId}
              style={{
                backgroundColor: theme.successColor,
                color: theme.textPrimaryColor,
                borderRadius: theme.badgeBorderRadius,
                padding: theme.badgePadding,
                fontSize: theme.badgeFontSize,
              }}
            >
              <ShoppingCart className="h-3.5 w-3.5" />
              Spesa
            </Button>
            <Button
              onClick={() => onModeChange('preparazione')}
              className={`tutorial-target-shoppingheader-mode h-auto px-2 py-1 text-xs flex items-center gap-1 ${mode === 'preparazione' ? '' : 'opacity-50'}`}
              aria-pressed={mode === 'preparazione'}
              aria-describedby={preparazioneDescId}
              style={{
                backgroundColor: theme.errorColor,
                color: theme.textPrimaryColor,
                borderRadius: theme.badgeBorderRadius,
                padding: theme.badgePadding,
                fontSize: theme.badgeFontSize,
              }}
            >
              <Home className="h-3.5 w-3.5" />
              Prepara
            </Button>
          </div>
          <span id={spesaDescId} className="sr-only">
            Visualizza la lista come elenco di prodotti da comprare.
          </span>
          <span id={preparazioneDescId} className="sr-only">
            Visualizza i prodotti organizzati per categoria prima della spesa.
          </span>
        </div>
      </div>

        
        <div className="flex items-end gap-[1px]">
          <Button
            variant="ghost"
            size="icon"
            aria-label={searchOpen ? 'Chiudi ricerca' : 'Apri ricerca'}
            className="tutorial-target-shoppingheader-search hover:bg-[var(--btn-bg-hover)] p-0 flex items-center justify-center focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary"
            onClick={handleToggleSearch}
            style={{
              borderRadius: theme.buttonBorderRadius || '0.375rem',
              transition: `all ${theme.transitionSpeed || '0.2s'} ease`,
              backgroundColor: 'transparent',
              color: theme.textPrimaryColor,
              '--btn-bg-hover': theme.primaryColor,
            } as React.CSSProperties}
          >
            {searchOpen ? (
              <X className="h-5 w-5" />
            ) : (
              <Search className="h-5 w-5" />
            )}
          </Button>
          
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                aria-label="Seleziona negozio"
                className="tutorial-target-shoppingheader-shops hover:bg-[var(--btn-bg-hover)] p-0 flex items-center justify-center focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary"
                style={{
                  borderRadius: theme.buttonBorderRadius || '0.375rem',
                  transition: `all ${theme.transitionSpeed || '0.2s'} ease`,
                  padding: theme.buttonPadding,
                  backgroundColor: 'transparent',
                  color: theme.textPrimaryColor,
                  '--btn-bg-hover': theme.primaryColor,
                } as React.CSSProperties}
              >
                <Store className="h-5 w-5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" style={{
              borderRadius: theme.borderRadius || '0.5rem',
              backgroundColor: theme.backgroundColor || '#ffffff',
              color: theme.textColor || '#000000',
            }}>
              <DropdownMenuItem
                className={!shopId ? "font-semibold" : ""}
                onClick={async () => {
                  if (!tryUseFeature('list_store_binding', { showToast: true })) {
                    return;
                  }

                  try {
                    await associateListWithShop({ listId, shopId: null });
                    onShopSelect(null);
                  } catch (err) {
                    console.error('associateListWithShop failed', err);
                  }
                }}
              >
                Nessun negozio
              </DropdownMenuItem>
              <Badge
                variant="secondary"
                className="text-xs"
                style={{
                  backgroundColor: DEFAULT_THEME.primaryColor,
                  color: DEFAULT_THEME.textPrimaryColor,
                  borderRadius: theme.badgeBorderRadius,
                  padding: theme.badgePadding,
                  fontSize: theme.badgeFontSize,
                }}
              >
                Premium
              </Badge>
              
              {shops?.map((shop) => (
                <DropdownMenuItem
                  key={shop.id}
                  className={shop.id === shopId ? "font-semibold" : ""}
                  onClick={async () => {
                    if (!tryUseFeature('list_store_binding', { showToast: true })) {
                      return;
                    }
                    try {
                      await associateListWithShop({ listId, shopId: shop.id });
                      await updateShoppingCategoriesFromShopOrdering(listId, shop.id);
                      onShopSelect(shop.id);
                    } catch (err) {
                      console.error('associateListWithShop failed', err);
                    }
                  }}
                >
                  {shop.shop_name}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
          
          <Button
            variant="ghost"
            size="icon"
            className="tutorial-target-shoppingheader-rapid hover:bg-white/20 p-0 flex items-center justify-center"
            onClick={toggleRapidInsert}
            aria-label={rapidInsert ? 'Disattiva inserimento rapido' : 'Attiva inserimento rapido'}
            aria-pressed={rapidInsert}
            style={{
              borderRadius: theme.buttonBorderRadius || '0.375rem',
              transition: `all ${theme.transitionSpeed || '0.2s'} ease`,
              backgroundColor: 'transparent',
              padding: theme.buttonPadding,
              color: theme.textPrimaryColor
            }}
          >
            <Zap fill={rapidInsert ? theme.textPrimaryColor : 'none'} className="h-5 w-5" />
          </Button>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                aria-label="Apri menu azioni lista"
                className="hover:bg-[var(--btn-bg-hover)] p-0 flex items-center justify-center"
                style={{
                  borderRadius: theme.buttonBorderRadius || '0.375rem',
                  transition: `all ${theme.transitionSpeed || '0.2s'} ease`,
                  backgroundColor: 'transparent',
                  color: theme.textPrimaryColor,
                  '--btn-bg-hover': theme.primaryColor,
                } as React.CSSProperties}
              >
                <MoreVertical className="h-5 w-5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" style={{
              borderRadius: theme.borderRadius || '0.5rem',
              backgroundColor: theme.backgroundColor || '#ffffff',
              color: theme.textColor || '#000000',
            }}>
              <DropdownMenuItem
                onSelect={() => {
                  void onExportList();
                }}
                className="gap-2"
                disabled={!canExport}
              >
                <Download className="h-4 w-4" />
                Esporta
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={() => onImportDialogOpenChange(true)} className="gap-2">
                <Upload className="h-4 w-4" />
                Importa
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onSelect={handleOpenTutorial} className="gap-2">
                <HelpCircle className="h-4 w-4" />
                Guida
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
      
      {selectedShop && (
  <div className="container max-w-md px-4 py-1 text-white text-sm" style={{
              backgroundColor: theme.warningColor,
            }}>
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-2">
        <div className="flex flex-col">
          <span>Negozio: {selectedShop.shop_name}</span>
        </div>
        {(selectedShop.latitude || selectedShop.address) && (
          <button
            onClick={() => {
              if (selectedShop.latitude && selectedShop.longitude) {
                window.open(`https://www.google.com/maps/search/?api=1&query=${selectedShop.latitude},${selectedShop.longitude}`, '_blank');
              } else if (selectedShop.address) {
                window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(selectedShop.address)}`, '_blank');
              }
            }}
            className="p-1.5 rounded-full"
            aria-label="Naviga verso il negozio"
            title="Naviga verso il negozio"
            style={{
              backgroundColor: theme.cardBackgroundColor,
              color: theme.primaryColor,
              borderRadius: theme.buttonBorderRadius,
              padding: theme.buttonPadding,
              transition: `all ${theme.transitionSpeed || '0.2s'} ease`
            }}
          >
            <NavigationIcon size={12} />
          </button>
        )}
      </div>

      <Button 
        variant="ghost"
        size="sm"
        className="p-0 h-auto"
        onClick={() => onShopSelect(null)}
        style={{
          borderRadius: theme.buttonBorderRadius,
          padding: theme.buttonPadding,
          transition: `all ${theme.transitionSpeed || '0.2s'} ease`
        }}
      >
        <X className="h-4 w-4" />
      </Button>
    </div>
  </div>
)}

      
      {searchOpen && (
        <div className="container max-w-md px-4 py-4 shadow-lg" style={{
          backgroundColor: theme.backgroundColor || '#ffffff',
          color: theme.textColor || '#000000',
        }}>
          <SearchBar 
            searchTerm={searchTerm} 
            setSearchTerm={setSearchTerm} 
            className="mb-2"
            style={{
              borderRadius: theme.inputBorderRadius || theme.borderRadius || '0.375rem',
            }}
          />
          
          <FilterButtons 
            currentFilter={filter} 
            setFilter={setFilter} 
            className="mb-2"
            style={{
              borderRadius: theme.buttonBorderRadius || '0.375rem',
            }}
          />
          
          <CategoryFilters
            selectedCategories={selectedCategories}
            setSelectedCategories={setSelectedCategories}
            shopId={shopId}
            style={{
              borderRadius: theme.buttonBorderRadius || '0.375rem',
            }}
          />


        </div>
      )}
      <TutorialSpotlight
        steps={shoppingHeaderTutorial}
        isOpen={isOpen}
        onClose={closeTutorial}
        pageId={pageId}
        onStepChange={onTutorialStepChange}
      />
    </header>
    <ImportItemsDialog open={isImportDialogOpen} onOpenChange={onImportDialogOpenChange} />
    {showVisualDebugger && <VisualDebugger />}
    <ImportListDialog
      isOpen={isImportDialogOpen}
      onOpenChange={onImportDialogOpenChange}
      listId={listId}
    />
    </>
  );
};

export default ShoppingHeader;
